import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Row, Col, Card, Spinner, Alert, Badge,
  Form, Button, Modal, Image
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthContext } from '../../contexts/AuthContext';
import UserContributions from '../../pages/user/UserContributions';
import DependentsManager from '../DependentsManager';


const UserProfile = ({ userId }) => {
  const { user, token } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    phone: '',
    address: '',
    department: '',
    date_of_birth: '',
    gender: '',
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Use logged-in user's ID if no userId prop is provided
  const profileUserId = userId || user?.id;

  useEffect(() => {
    if (profileUserId && token) {
      fetchProfile();
    } else if (!token) {
      setError('Authentication required. Please log in.');
      setLoading(false);
    }
  }, [profileUserId, token]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://aceswafund.org/welfare/welfare-system/get-profile?user_id=${profileUserId}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please log in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        setProfile(result.data);
        // Initialize edit form with current profile data
        setEditFormData({
          full_name: result.data.full_name || '',
          phone: result.data.phone || '',
          address: result.data.address || '',
          department: result.data.department || '',
          date_of_birth: result.data.date_of_birth === '0000-00-00' ? '' : result.data.date_of_birth,
          gender: result.data.gender || '',
        });
        setError(null);
      } else {
        setError(result.message || 'Failed to load profile');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching profile');
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
    setUpdateSuccess(false);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedImage(null);
    setImagePreview(null);
    setUpdateSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);

    try {
      const formData = new FormData();

      // Append all form fields
      Object.keys(editFormData).forEach(key => {
        if (editFormData[key]) {
          formData.append(key, editFormData[key]);
        }
      });

      // Append image if selected
      if (selectedImage) {
        formData.append('profile_image', selectedImage);
      }

      const response = await fetch('https://aceswafund.org/welfare/welfare-system/update-profile', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,

        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please log in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        setUpdateSuccess(true);
        // Refresh profile data
        await fetchProfile();

        // Close modal after 1.5 seconds
        setTimeout(() => {
          handleCloseModal();
        }, 1500);
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while updating profile');
      console.error('Profile update error:', err);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateForInput = (dateString) => {
    if (!dateString || dateString === '0000-00-00') return '';
    return dateString;
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'No contributions yet';
    return `GHS ${parseFloat(amount).toFixed(2)}`;
  };

  const getInitials = (fullName) => {
    if (!fullName) return 'U';
    return fullName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeVariant = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'danger';
      case 'supervisor':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getStatusBadgeVariant = (isActive) => {
    return isActive ? 'success' : 'secondary';
  };

  // Check if current user can edit this profile
  const canEdit = user && (user.id === profileUserId || user.role === 'admin');

  if (!token) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Authentication Required</Alert.Heading>
          <p>Please log in to view your profile.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading profile...</p>
        </div>
      </Container>
    );
  }

  if (error && !profile) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Profile</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Profile Not Found</Alert.Heading>
          <p>Unable to load profile information.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">

      <Card className="shadow-sm border-0">
        <Card.Body className="p-4">
          {/* Profile Header with Edit Button */}
          <Row className="align-items-center mb-4">
            <Col xs={12} md={3} className="text-center mb-3 mb-md-0">
              <div className="position-relative d-inline-block">
                <Image
                  src={user.profile_url}
                  alt={profile.full_name}
                  className="rounded-circle border shadow-sm"
                  style={{
                    width: '150px',
                    height: '150px',
                    objectFit: 'cover',
                    border: '3px solid #fff'
                  }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://aceswafund.org/welfare/welfare-system/uploads/profile_images/default_profile.png';
                  }}
                />
                <div
                  className="rounded-circle bg-light border shadow-sm d-none"
                  style={{
                    width: '150px',
                    height: '150px',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: '#6c757d'
                  }}
                >
                  {getInitials(profile.full_name)}
                </div>
              </div>
            </Col>

            <Col xs={12} md={9}>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <div>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <h2 className="mb-0 me-2">{profile.full_name}</h2>
                    <Badge bg={getRoleBadgeVariant(profile.role)}>
                      {profile.role || 'User'}
                    </Badge>
                    <Badge bg={getStatusBadgeVariant(profile.is_active)}>
                      {profile.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>

                  <p className="text-muted mb-2">
                    <strong>User ID:</strong> {profile.custom_id}
                  </p>

                  <p className="text-muted mb-0">
                    <strong>Member since:</strong> {formatDate(profile.created_at)}
                  </p>
                </div>

                {canEdit && (
                  <Button
                    variant="primary"
                    onClick={handleEditClick}
                    className="mt-3 mt-md-0"
                  >
                    <i className="bi bi-pencil-square me-2"></i>
                    Edit Profile
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          {/* Profile Information Grid */}
          <Row className="g-4">
            {/* Personal Information */}
            <Col md={6}>
              <Card className="h-100 border-0 bg-light">
                <Card.Body>
                  <h5 className="border-bottom pb-2 mb-3">Personal Information</h5>

                  <div className="mb-2">
                    <small className="text-muted d-block">Email</small>
                    <strong>{profile.email || 'Not provided'}</strong>
                  </div>

                  <div className="mb-2">
                    <small className="text-muted d-block">Phone</small>
                    <strong>{profile.phone || 'Not provided'}</strong>
                  </div>

                  <div className="mb-2">
                    <small className="text-muted d-block">Address</small>
                    <strong>{profile.address || 'Not provided'}</strong>
                  </div>

                  <div className="mb-2">
                    <small className="text-muted d-block">Gender</small>
                    <strong>{profile.gender || 'Not provided'}</strong>
                  </div>

                  <div className="mb-2">
                    <small className="text-muted d-block">Date of Birth</small>
                    <strong>{formatDate(profile.date_of_birth)}</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Work Information */}
            <Col md={6}>
              <Card className="h-100 border-0 bg-light">
                <Card.Body>
                  <h5 className="border-bottom pb-2 mb-3">Work Information</h5>

                  <div className="mb-2">
                    <small className="text-muted d-block">Department</small>
                    <strong>{profile.department || 'Not assigned'}</strong>
                  </div>

                  <div className="mb-2">
                    <small className="text-muted d-block">Position</small>
                    <strong>{profile.position || 'Not assigned'}</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Contribution Information */}
            <Col md={12}>
              <Card className="border-0 bg-light">
                <Card.Body>
                  <h5 className="border-bottom pb-2 mb-3">Contribution Details</h5>

                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <small className="text-muted d-block">Contribution Category</small>
                        <strong>{profile.contribution_category ? `Category ${profile.contribution_category}` : 'No active contribution'}</strong>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-2">
                        <small className="text-muted d-block">Contribution Amount</small>
                        <strong>{formatCurrency(profile.contribution_amount)}</strong>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>

      </Card>

      <div className="card shadow-sm border-0">
        <DependentsManager />
      </div>

      <UserContributions />

      {/* Edit Profile Modal */}
      <Modal show={showEditModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Profile</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmitUpdate}>
          <Modal.Body>
            {updateSuccess && (
              <Alert variant="success">
                Profile updated successfully!
              </Alert>
            )}

            {error && (
              <Alert variant="danger">
                {error}
              </Alert>
            )}

            <Row>
              <Col md={4} className="text-center mb-3">
                <div className="mb-3">
                  <Image
                    src={user.profile_url}
                    alt={profile.full_name}
                    className="rounded-circle border shadow-sm"
                    style={{
                      width: '150px',
                      height: '150px',
                      objectFit: 'cover',
                      border: '3px solid #fff'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://aceswafund.org/welfare/welfare-system/uploads/profile_images/default_profile.png';
                    }}
                  />
                </div>
                <Form.Group controlId="profile_image">
                  <Form.Label className="btn btn-outline-primary btn-sm">
                    Change Photo
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: 'none' }}
                    />
                  </Form.Label>
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="full_name"
                    value={editFormData.full_name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={editFormData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_of_birth"
                        value={formatDateForInput(editFormData.date_of_birth)}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={editFormData.address}
                    onChange={handleInputChange}
                    placeholder="Enter address"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Department</Form.Label>
                      <Form.Control
                        type="text"
                        name="department"
                        value={editFormData.department}
                        onChange={handleInputChange}
                        placeholder="Enter department"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Gender</Form.Label>
                      <Form.Select
                        name="gender"
                        value={editFormData.gender}
                        onChange={handleInputChange}
                      >
                        <option value="">Select gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={updating}
            >
              {updating ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Updating...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
};

export default UserProfile;